/**
 * Frontend mirror of the backend `autoGranularity()` helper
 * (`src/services/analytics/queries.ts`) so the Overview page can display
 * the *effective* granularity even when the user picks "Auto".
 *
 * Source of truth lives server-side; when the backend rule changes, mirror
 * it here. Divergence only affects the pre-flight label text — the server
 * still pins the real bucket size on the data response, so UI recovers on
 * the next render.
 *
 * Boundaries (matching backend, RESEARCH §P8):
 *   < 2 days  → hourly
 *   ≤ 60 days → daily
 *   > 60 days → weekly
 */

export type Granularity = 'hourly' | 'daily' | 'weekly';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Resolve the effective bucket size for a given filter granularity + range.
 *
 * @param granularity Filter value — 'auto' consults the range span; anything
 *   else is passed through as-is.
 * @param from ISO-date `YYYY-MM-DD` string (inclusive range start).
 * @param to   ISO-date `YYYY-MM-DD` string (inclusive range end).
 */
export function effectiveGranularity(
  granularity: 'auto' | Granularity,
  from: string,
  to: string,
): Granularity {
  if (granularity !== 'auto') return granularity;
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return 'daily';
  const span = toMs - fromMs;
  if (span < 2 * DAY_MS) return 'hourly';
  if (span <= 60 * DAY_MS) return 'daily';
  return 'weekly';
}
